// PausaInactividadGate.js
//
// Cubre R25, R26, R27, R28 y R30 — .claude/specs/1583-promo-octubre/
// Mockup elegido: opción A, "modal centrado sobre la app difuminada"
// (tools/mockups/smartdx_mockups_pausa_inactividad.html), tres estados:
//   1. pausada     → "Tu cuenta está en pausa" + "Solicitar 7 días más gratis"
//   2. revisando   → "Estamos revisando tu solicitud" + "Cerrar sesión"
//   3. reactivada  → aviso de una sola vez, "Ir al Turno" (al volver a entrar)
//
// Regla de la cuenta gratis (09-oct-2026): 7 días sin generar un documento con
// IA la pausan. El back la calcula por fechas (`billing.access.access_state`,
// code `inactividad`) y la reactiva solo a los 3 minutos de la solicitud, pero
// la app NUNCA lo dice (v2, Sebastián): "revisando" no trae plazo, ni cuenta
// regresiva, ni botón para pedir de nuevo, y no hay polling. El médico se entera
// por el correo automático y por el aviso al volver a entrar.
//
// Relectura de `/me` (la única que hace este componente):
//   - un 403 `inactividad` a mitad de sesión (`smartdx:acceso-bloqueado`, lo
//     emite get-client.js): UNA vez por racha, como TosAcceptanceGate;
//
// La fuente de verdad es la ÚLTIMA lectura de `/me`, no una marca pegajosa (R27,
// ronda 2 de #1583). Al cargar /turno en pausa llegan ~10 403 en ~150 ms; una
// marca booleana que solo baja con la primera relectura quedaba en `true` por los
// que aterrizaban después, y con `/me` ya en `reactivada` volver a la pestaña no
// levantaba el overlay. Ahora cada 403 trae el instante en que SALIÓ su request
// (`enviadoEn`) y cada lectura de `/me` el instante en que se pidió: el 403 solo
// cuenta si salió después de la última lectura aplicada. Un 403 más viejo que esa
// lectura no puede re-bloquear.
//   - estando en "revisando", al volver a ver la pestaña (`visibilitychange` a
//     `visible`): UNA por regreso. Con la pestaña a la vista no se lee nada.
//
// Trampa de foco (#1534, INC-2026-52): SuscripcionGate y los demás gates son un
// `Box` fijo de z 20000, no un Modal; si el 403 llega con un Dialog de MUI ya
// abierto (selector, intro, censo), ese Dialog conserva su focus trap y el botón
// de acá no recibe el teclado. Este gate se dibuja dentro de un `Modal` de MUI:
// el último Modal montado gana el trap, así que el botón queda operable por
// teclado aunque haya un diálogo debajo. `hideBackdrop` y backdrop propio solo
// para controlar el difuminado del mockup con tokens.
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Link, Modal, SvgIcon, Typography } from "@mui/material";
import PauseCircleOutlineOutlinedIcon from "@mui/icons-material/PauseCircleOutlineOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import { useCurrentUser } from "../context/UserContext";
import getClient from "../../helpers/get-client";
import { logout } from "../../helpers/authentication-helpers";
import { WHATSAPP_SOPORTE } from "../../helpers/soporte";
import { fechaLarga } from "../../helpers/trial-fecha";
import { CODE_INACTIVIDAD, avisoReactivadaVisible, estadoPausaVisible } from "../../helpers/pausa";

const client = getClient();

// Avión de papel del mockup A (trazo, no relleno): el `SendOutlined` de MUI es
// otro dibujo y el mockup aprobado manda el ícono.
const EnviarIcon = (props) => (
  <SvgIcon
    {...props}
    viewBox="0 0 24 24"
    sx={{ fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round", ...props.sx }}
  >
    <path d="M14.5 21.7a.5.5 0 0 0 .94-.03L22 2.5a.5.5 0 0 0-.7-.7L2.3 8.56a.5.5 0 0 0-.03.94l7.93 3.18a2 2 0 0 1 1.1 1.11z" />
    <path d="m21.85 2.15-10.94 10.94" />
  </SvgIcon>
);

// Tonos del ícono (mockup: `.ico.warn|info|ok`, 46 px, radio 12).
const TONOS = {
  warn: { rgb: "--color-warning-rgb", color: "var(--color-warning)" },
  info: { rgb: "--color-info-rgb", color: "var(--color-info)" },
  ok: { rgb: "--color-success-rgb", color: "var(--color-success)" },
};

const Icono = ({ tono, children }) => (
  <Box
    sx={{
      display: "grid",
      placeItems: "center",
      width: 46,
      height: 46,
      borderRadius: "12px",
      flex: "none",
      mb: "14px",
      color: TONOS[tono].color,
      backgroundColor: `rgba(var(${TONOS[tono].rgb}), 0.14)`,
      border: `1px solid rgba(var(${TONOS[tono].rgb}), 0.35)`,
      "& svg": { fontSize: 24 },
    }}
  >
    {children}
  </Box>
);

const Eyebrow = ({ children }) => (
  <Typography
    sx={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.6px",
      textTransform: "uppercase",
      color: "var(--text-muted)",
    }}
  >
    {children}
  </Typography>
);

const Titulo = ({ children }) => (
  <Typography
    component="h2"
    sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2, mt: "4px", color: "var(--text-primary)" }}
  >
    {children}
  </Typography>
);

const Texto = ({ children }) => (
  <Typography sx={{ fontSize: 14, color: "var(--text-secondary)", mt: 1, lineHeight: 1.45 }}>
    {children}
  </Typography>
);

const BotonPrincipal = ({ children, startIcon, endIcon, ...props }) => (
  <Button
    variant="contained"
    disableElevation
    disableRipple
    fullWidth
    startIcon={startIcon}
    endIcon={endIcon}
    {...props}
    sx={{
      mt: 2,
      py: "11px",
      px: 2,
      borderRadius: "10px",
      textTransform: "none",
      fontWeight: 700,
      fontSize: 14,
      lineHeight: 1.45,
      backgroundColor: "var(--color-primary)",
      color: "var(--text-on-primary)",
      "&:hover": { backgroundColor: "var(--color-primary-soft)" },
      "&.Mui-focusVisible": { outline: "2px solid var(--color-primary-soft)", outlineOffset: 2 },
      "&.Mui-disabled": { backgroundColor: "rgba(var(--fg-rgb), 0.10)", color: "var(--text-muted)" },
    }}
  >
    {children}
  </Button>
);

const LinkSoporte = () => (
  <Link
    data-testid="pausa-soporte"
    href={WHATSAPP_SOPORTE}
    target="_blank"
    rel="noopener noreferrer"
    underline="none"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      mt: "12px",
      fontSize: 13,
      fontWeight: 600,
      color: "var(--accent-onboarding)",
    }}
  >
    <WhatsAppIcon sx={{ fontSize: 15 }} />
    Tengo problemas, escríbeme por WhatsApp
  </Link>
);

// Modal centrado del mockup A: la app queda detrás, difuminada. `Modal` de MUI
// (ver "Trampa de foco" arriba), sin cierre por Escape ni por click afuera: la
// pantalla no se descarta, se resuelve.
const MarcoModal = ({ testId, children }) => (
  <Modal
    open
    hideBackdrop
    disableEscapeKeyDown
    data-testid={testId}
    sx={{ zIndex: 20000 }}
  >
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        display: "grid",
        placeItems: "center",
        p: 2,
        overflowY: "auto",
        outline: "none",
        backgroundColor: "rgba(var(--shadow-rgb), 0.55)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
      }}
    >
      <Box
        role="dialog"
        aria-modal="true"
        sx={{
          width: "100%",
          maxWidth: 420,
          boxSizing: "border-box",
          p: { xs: "20px", sm: "24px" },
          backgroundColor: "var(--bg-elevated)",
          border: "1px solid var(--border-strong)",
          borderRadius: "16px",
          boxShadow: "0 24px 60px rgba(var(--shadow-rgb), 0.4)",
          color: "var(--text-primary)",
          my: "auto",
        }}
      >
        {children}
      </Box>
    </Box>
  </Modal>
);

const PausaInactividadGate = () => {
  const { currentUser, loading, refreshUser } = useCurrentUser();
  const navigate = useNavigate();
  // Llegó un 403 `inactividad` a mitad de sesión y el `/me` guardado puede estar
  // viejo (todavía dice que sí puede entrar).
  // Instante (performance.now) del request más reciente que devolvió 403
  // `inactividad`, y del último `/me` que se aplicó. `bloqueoVivo` se deriva de
  // comparar los dos, nunca se guarda: no hay marca que se quede pegada.
  const [bloqueoEn, setBloqueoEn] = useState(-1);
  const [meEn, setMeEn] = useState(-1);
  const bloqueoVivo = bloqueoEn > meEn;
  // El médico apretó "Solicitar" y el back contestó `revisando`: la pantalla pasa
  // a "revisando" sin esperar otra lectura de `/me`.
  const [solicitada, setSolicitada] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  // Cierre local del aviso: se esconde al toque aunque el POST tarde.
  const [avisoCerrado, setAvisoCerrado] = useState(false);
  const yaRefrescado = useRef(false);
  const leyendoAlVolver = useRef(false);

  // Toda relectura de `/me` de este componente pasa por acá: anota cuándo se
  // pidió y, si llegó bien, la deja como la última verdad conocida.
  const leerMe = useCallback(async () => {
    const pedidoEn = performance.now();
    const user = await refreshUser?.();
    setMeEn((previo) => Math.max(previo, pedidoEn));
    return user;
  }, [refreshUser]);

  const base = !loading ? estadoPausaVisible(currentUser, bloqueoVivo) : null;
  const estado = base && solicitada ? "revisando" : base;
  const visible = estado !== null;

  // Un 403 `inactividad` en vivo: se muestra el gate y se relee `/me` UNA vez por
  // racha (con el overlay arriba, el fondo seguiría disparando 403 de la app).
  useEffect(() => {
    const onBloqueo = (event) => {
      if (event?.detail?.code !== CODE_INACTIVIDAD) return;
      // Eventos sin `enviadoEn` (otro emisor) se tratan como "salió ahora".
      const salioEn = Number.isFinite(event.detail.enviadoEn) ? event.detail.enviadoEn : performance.now();
      setBloqueoEn((previo) => Math.max(previo, salioEn));
      if (yaRefrescado.current) return;
      yaRefrescado.current = true;
      leerMe().catch(() => {});
    };
    window.addEventListener("smartdx:acceso-bloqueado", onBloqueo);
    return () => window.removeEventListener("smartdx:acceso-bloqueado", onBloqueo);
  }, [leerMe]);

  // Al salir de la pausa se limpia todo para que un ciclo nuevo (otra pausa
  // días después) arranque desde "pausada".
  useEffect(() => {
    if (visible) return;
    yaRefrescado.current = false;
    setSolicitada(false);
    setEnviando(false);
    setError(null);
  }, [visible]);

  // R27: en "revisando", al volver a ver la pestaña se lee `/me` una vez. Sin
  // temporizador: con la pestaña a la vista no hay ninguna lectura.
  const revisando = estado === "revisando";
  useEffect(() => {
    if (!revisando) return undefined;
    const alVolver = () => {
      if (document.visibilityState !== "visible") return;
      if (leyendoAlVolver.current) return;
      leyendoAlVolver.current = true;
      leerMe()
        .catch(() => {})
        .finally(() => {
          leyendoAlVolver.current = false;
        });
    };
    document.addEventListener("visibilitychange", alVolver);
    return () => document.removeEventListener("visibilitychange", alVolver);
  }, [revisando, leerMe]);

  const solicitar = useCallback(async () => {
    setEnviando(true);
    setError(null);
    try {
      const res = await client.post("/api/user/me/pausa/solicitar/");
      if (res?.data?.pausa?.estado === "revisando") {
        setSolicitada(true);
      } else {
        await leerMe();
      }
    } catch (e) {
      if (e?.response?.status === 409) {
        // Ya no está pausada (otra pestaña, un admin): el `/me` decide.
        try {
          await leerMe();
        } catch {
          /* sin red: el médico puede reintentar */
        }
      } else {
        setError("No pudimos enviar tu solicitud. Revisa tu conexión y reintenta.");
      }
    } finally {
      setEnviando(false);
    }
  }, [leerMe]);

  const mostrarAviso = !loading && !visible && !avisoCerrado && avisoReactivadaVisible(currentUser);

  // Tras un `/me` que ya trae `aviso_reactivada: false`, el cierre local se
  // suelta para que una solicitud futura pueda volver a mostrar el aviso.
  const avisoDelServidor = currentUser?.access?.pausa?.aviso_reactivada === true;
  useEffect(() => {
    if (!avisoDelServidor) setAvisoCerrado(false);
  }, [avisoDelServidor]);

  const cerrarAviso = useCallback(
    async (irAlTurno) => {
      setAvisoCerrado(true);
      try {
        await client.post("/api/user/me/pausa/aviso-visto/");
        await leerMe();
      } catch {
        /* el aviso ya se cerró acá; si falla, vuelve en la próxima entrada */
      }
      if (irAlTurno) navigate("/turno");
    },
    [navigate, leerMe],
  );

  if (visible) {
    if (estado === "revisando") {
      return (
        <MarcoModal testId="pausa-inactividad-gate">
          <Box data-testid="pausa-revisando">
            <Icono tono="info">
              <HourglassEmptyOutlinedIcon />
            </Icono>
            <Eyebrow>Solicitud enviada</Eyebrow>
            <Titulo>Estamos revisando tu solicitud</Titulo>
            <Texto>Recibimos tu solicitud. Revisaremos tu caso y te responderemos por correo.</Texto>
            <Button
              data-testid="pausa-cerrar-sesion"
              disableRipple
              onClick={logout}
              startIcon={<LogoutOutlinedIcon sx={{ fontSize: 15 }} />}
              fullWidth
              sx={{
                mt: 2,
                p: "10px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: 13,
                color: "var(--text-muted)",
                borderRadius: "10px",
                "&:hover": { backgroundColor: "rgba(var(--fg-rgb), 0.05)" },
                "&.Mui-focusVisible": { outline: "2px solid var(--color-primary-soft)", outlineOffset: 2 },
              }}
            >
              Cerrar sesión
            </Button>
          </Box>
        </MarcoModal>
      );
    }
    return (
      <MarcoModal testId="pausa-inactividad-gate">
        <Box data-testid="pausa-pausada">
          <Icono tono="warn">
            <PauseCircleOutlineOutlinedIcon />
          </Icono>
          <Eyebrow>Modo Turno · cuenta en pausa</Eyebrow>
          <Titulo>Tu cuenta está en pausa</Titulo>
          <Texto>
            Pasaron 7 días sin generar documentos, así que pausamos tu cuenta gratis. Puedes pedir 7
            días más y la revisamos.
          </Texto>
          <Box
            sx={{
              display: "flex",
              gap: "10px",
              alignItems: "flex-start",
              p: "10px 12px",
              mt: "14px",
              borderRadius: "12px",
              fontSize: 12.5,
              color: "var(--text-secondary)",
              backgroundColor: "rgba(var(--color-success-rgb), 0.08)",
              border: "1px solid rgba(var(--color-success-rgb), 0.25)",
            }}
          >
            <GppGoodOutlinedIcon sx={{ fontSize: 18, color: "var(--color-success)", flex: "none", mt: "1px" }} />
            <span>
              <b>Tu trabajo guardado sigue ahí.</b> Ningún turno, cama ni nota se borra por esto.
            </span>
          </Box>
          {error && (
            <Typography
              role="alert"
              data-testid="pausa-error"
              sx={{ fontSize: 12.5, color: "var(--color-danger)", mt: 1.5 }}
            >
              {error}
            </Typography>
          )}
          <BotonPrincipal
            data-testid="pausa-solicitar"
            onClick={solicitar}
            disabled={enviando}
            startIcon={<EnviarIcon sx={{ fontSize: 17 }} />}
          >
            Solicitar 7 días más gratis
          </BotonPrincipal>
          <LinkSoporte />
        </Box>
      </MarcoModal>
    );
  }

  if (mostrarAviso) {
    const hasta = currentUser?.access?.pausa?.ventana_hasta;
    return (
      <MarcoModal testId="pausa-aviso-reactivada">
        <Icono tono="ok">
          <CheckCircleOutlineOutlinedIcon />
        </Icono>
        <Eyebrow>Cuenta reactivada</Eyebrow>
        <Titulo>Revisamos tu solicitud, te dimos 7 días más gratis</Titulo>
        <Texto>Tu cuenta está activa de nuevo. Si tienes problemas, contáctanos.</Texto>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: "14px" }}>
          {[
            "7 días más gratis",
            hasta ? `Hasta el ${fechaLarga(hasta)}` : null,
          ]
            .filter(Boolean)
            .map((t) => (
              <Box
                key={t}
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  p: "5px 11px",
                  borderRadius: 999,
                  color: "var(--text-secondary)",
                  backgroundColor: "rgba(var(--fg-rgb), 0.07)",
                }}
              >
                {t}
              </Box>
            ))}
        </Box>
        <BotonPrincipal
          data-testid="pausa-aviso-cerrar"
          onClick={() => cerrarAviso(true)}
          endIcon={<ArrowForwardOutlinedIcon sx={{ fontSize: 17 }} />}
        >
          Ir al Turno
        </BotonPrincipal>
        <LinkSoporte />
      </MarcoModal>
    );
  }

  return null;
};

export default PausaInactividadGate;
